import { FC } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Icon,
  useColorModeValue,
  SimpleGrid,
  Card,
  Heading
} from '@chakra-ui/react';
import { MdArrowBack, MdVideoLibrary, MdLibraryBooks, MdQuiz } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ADMIN_ROUTES } from '@bemap/utils';
import { PageHeading, Panel } from '@bemap/ui';

const SelectContentTypePage: FC = () => {
  const navigate = useNavigate();
  const { courseId, moduleId } = useParams<{ courseId: string; moduleId: string }>();
  const { t } = useTranslation();

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const handleBack = () => {
    navigate(`${ADMIN_ROUTES.UNIVERSITY_COURSES}/${courseId}`);
  };

  const handleSelectContentType = (contentType: string) => {
    switch (contentType) {
      case 'video':
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_VIDEO_CREATE.replace(':courseId', courseId!).replace(':moduleId', moduleId!));
        break;
      case 'document':
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_DOCUMENT_CREATE.replace(':courseId', courseId!).replace(':moduleId', moduleId!));
        break;
      case 'quiz':
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_QUIZ_CREATE.replace(':courseId', courseId!).replace(':moduleId', moduleId!));
        break;
      default:
        navigate(ADMIN_ROUTES.UNIVERSITY_COURSES_MODULE_CONTENT_DOCUMENT_CREATE.replace(':courseId', courseId!).replace(':moduleId', moduleId!));
    }
  };

  const contentTypes = [
    {
      type: 'video',
      title: t('university.content_types.video.title'),
      description: t('university.content_types.video.description'),
      icon: MdVideoLibrary,
      color: 'red',
      features: [
        t('university.content_types.video.features.0'),
        t('university.content_types.video.features.1'),
        t('university.content_types.video.features.2')
      ]
    },
    {
      type: 'document',
      title: t('university.content_types.document.title'),
      description: t('university.content_types.document.description'),
      icon: MdLibraryBooks,
      color: 'blue',
      features: [
        t('university.content_types.document.features.0'),
        t('university.content_types.document.features.1'),
        t('university.content_types.document.features.2')
      ]
    },
    {
      type: 'quiz',
      title: t('university.content_types.quiz.title'),
      description: t('university.content_types.quiz.description'),
      icon: MdQuiz,
      color: 'green',
      features: [
        t('university.content_types.quiz.features.0'),
        t('university.content_types.quiz.features.1'),
        t('university.content_types.quiz.features.2')
      ]
    }
  ];

  return (
    <VStack align="stretch" spacing={6}>
      {/* Header */}
      <PageHeading title={t('university.content_types.select_content_type')} actions={[
        {
          label: t('university.content_types.back_to_course'),
          onClick: handleBack,
          icon: <Icon as={MdArrowBack} />,
          variant: 'outline'
        }
      ]} />

      {/* Content Type Selection */}
      <VStack align="stretch" spacing={6}>
        <Box textAlign="center">
          <Heading size="lg" mb={2}>
            {t('university.content_types.choose_content_type')}
          </Heading>
          <Text color="gray.600" fontSize="lg">
            {t('university.content_types.select_best_option')}
          </Text>
        </Box>

        <Panel heading={t('university.content_types.available_content_types')} padding={6}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {contentTypes.map((contentType) => (
              <Card
                key={contentType.type}
                bg={cardBg}
                border="1px"
                borderColor={borderColor}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{
                  transform: 'translateY(-2px)',
                  borderColor: `${contentType.color}.300`,
                  boxShadow: 'lg'
                }}
                onClick={() => handleSelectContentType(contentType.type)}
              >
                <VStack align="stretch" spacing={4} p={6}>
                  <Box textAlign="center">
                    <Icon
                      as={contentType.icon}
                      boxSize={12}
                      color={`${contentType.color}.500`}
                      mb={3}
                    />
                    <Heading size="md" mb={2}>
                      {contentType.title}
                    </Heading>
                    <Text color="gray.600">
                      {contentType.description}
                    </Text>
                  </Box>

                  <VStack align="start" spacing={2}>
                    <Text fontSize="sm" fontWeight="medium" color="gray.700">
                      {t('university.content_types.features')}:
                    </Text>
                    {contentType.features.map((feature, index) => (
                      <HStack key={index} spacing={2}>
                        <Box
                          w={2}
                          h={2}
                          borderRadius="full"
                          bg={`${contentType.color}.500`}
                        />
                        <Text fontSize="sm" color="gray.600">
                          {feature}
                        </Text>
                      </HStack>
                    ))}
                  </VStack>

                  <Button
                    colorScheme={contentType.color}
                    variant="outline"
                    size="lg"
                    w="full"
                    mt={4}
                  >
                    {t('university.content_types.select')} {contentType.title}
                  </Button>
                </VStack>
              </Card>
            ))}
          </SimpleGrid>
        </Panel>

        <Box textAlign="center" pt={4}>
          <Text color="gray.500" fontSize="sm">
            {t('university.content_types.need_help')}{' '}
            <Text as="span" color="blue.500" cursor="pointer">
              {t('university.content_types.contact_support')}
            </Text>
          </Text>
        </Box>
      </VStack>
    </VStack>
  );
};

export default SelectContentTypePage; 